/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {string} The key used to store product details cache */
  static #CACHE_KEY = 'viewedProductsCache';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list with full product data.
   * @param {string} productId - The ID of the product to add.
   * @param {Object} productData - The full product data (title, price, image, url, handle).
   */
  static addProduct(productId, productData = null) {
    let viewedProducts = this.getProductIds();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));

    // Cache product data if provided
    if (productData) {
      this.cacheProductData(productId, productData);
    }
  }

  /**
   * Cache product data for quick retrieval
   * @param {string} productId - The product ID
   * @param {Object} productData - The product data to cache
   */
  static cacheProductData(productId, productData) {
    let cache = JSON.parse(localStorage.getItem(this.#CACHE_KEY) || '{}');
    cache[productId] = {
      ...productData,
      cachedAt: Date.now()
    };
    localStorage.setItem(this.#CACHE_KEY, JSON.stringify(cache));
  }

  /**
   * Get cached product data
   * @param {string} productId - The product ID
   * @returns {Object|null} Cached product data or null
   */
  static getCachedProductData(productId) {
    const cache = JSON.parse(localStorage.getItem(this.#CACHE_KEY) || '{}');
    const cached = cache[productId];
    
    // Cache expires after 24 hours
    if (cached && (Date.now() - cached.cachedAt) < 24 * 60 * 60 * 1000) {
      // For older cache entries (> 1 hour), mark for revalidation
      if ((Date.now() - cached.cachedAt) > 60 * 60 * 1000) {
        cached.needsRevalidation = true;
      }
      return cached;
    }
    return null;
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
    localStorage.removeItem(this.#CACHE_KEY);
  }

  /**
   * Retrieves the list of recently viewed product IDs.
   * @returns {string[]} The list of viewed product IDs.
   */
  static getProductIds() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }

  /**
   * Legacy method for backwards compatibility
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return this.getProductIds();
  }

  /**
   * Get recently viewed products with full product data
   * @returns {Promise<Array>} Array of product objects with full data
   */
  static async getProductsWithData() {
    const productIds = this.getProductIds();
    const products = [];
    const invalidProductIds = [];

    for (const productId of productIds) {
      // Try to get from cache first
      let productData = this.getCachedProductData(productId);
      
      if (!productData) {
        // If not cached, try to fetch from Shopify
        try {
          productData = await this.fetchProductData(productId);
          if (productData) {
            this.cacheProductData(productId, productData);
          }
        } catch (error) {
          console.error('Failed to fetch product data for ID:', productId, error);
          invalidProductIds.push(productId);
          continue;
        }
      }

      if (productData) {
        products.push({
          id: productId,
          title: productData.title,
          price: productData.price,
          image: productData.image,
          url: productData.url,
          handle: productData.handle
        });
      } else {
        // Product no longer exists or is unavailable
        invalidProductIds.push(productId);
      }
    }

    // Clean up invalid products from storage
    if (invalidProductIds.length > 0) {
      this.removeInvalidProducts(invalidProductIds);
    }

    return products;
  }

  /**
   * Remove invalid product IDs from storage
   * @param {string[]} invalidIds - Array of product IDs to remove
   */
  static removeInvalidProducts(invalidIds) {
    let viewedProducts = this.getProductIds();
    viewedProducts = viewedProducts.filter(id => !invalidIds.includes(id));
    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    
    // Also remove from cache
    let cache = JSON.parse(localStorage.getItem(this.#CACHE_KEY) || '{}');
    invalidIds.forEach(id => delete cache[id]);
    localStorage.setItem(this.#CACHE_KEY, JSON.stringify(cache));
  }

  /**
   * Fetch product data from Shopify API
   * @param {string} productId - The product ID
   * @returns {Promise<Object|null>} Product data or null
   */
  static async fetchProductData(productId) {
    try {
      // Try to fetch from the products list API
      const response = await fetch(`/products.json?limit=250`);
      if (!response.ok) {
        return null;
      }
      
      const data = await response.json();
      const product = data.products.find(p => p.id.toString() === productId.toString());
      
      if (product) {
        return {
          title: product.title,
          price: product.variants[0]?.price || '0.00',
          image: product.images[0]?.src || '',
          url: `/products/${product.handle}`,
          handle: product.handle
        };
      }
      
      // Product not found in list - it might be deleted or beyond the 250 limit
      return null;
    } catch (error) {
      console.error('Error fetching product data for ID:', productId, error);
      return null;
    }
  }
}

/**
 * Global interface for recently viewed products (for backwards compatibility with carousel)
 */
window.RecentlyViewedSimple = {
  /**
   * Get recently viewed products with full data for display
   * @returns {Promise<Array>} Array of product objects
   */
  getProducts: async function() {
    return await RecentlyViewed.getProductsWithData();
  },

  /**
   * Add a product to recently viewed
   * @param {string} productId - Product ID
   * @param {Object} productData - Product data
   */
  addProduct: function(productId, productData) {
    RecentlyViewed.addProduct(productId, productData);
  },

  /**
   * Clear all recently viewed products
   */
  clearProducts: function() {
    RecentlyViewed.clearProducts();
  }
};
